import { hasError, type Result } from "operation-result.js";
import { SubtreeController } from "subtree.js/core";
import type { AppDeps } from "../../app-deps";
import {
  SignInCancelled,
  type SignInFailed,
  signInWithApple,
  signInWithGoogle,
  signUpWithEmail,
} from "../../core/ops/auth-ops";
import { RegisterActions, RegisterState } from "./register-model";

export type RegisterDeps = Pick<AppDeps, "client" | "session" | "profileRepository">;

/** No routing: the app root's gate flips to `signedIn` once the profile loads, and the route component moves on. */
export class RegisterController extends SubtreeController implements RegisterActions {
  readonly state = new RegisterState();

  constructor(readonly deps: RegisterDeps) {
    super();
    this.subtree.put(RegisterState, this.state);
    this.subtree.put(RegisterActions, this);
  }

  setName = (value: string) => {
    this.state.draft.update((d) => ({ ...d, name: value }));
  };

  setEmail = (value: string) => {
    this.state.draft.update((d) => ({ ...d, email: value }));
  };

  setPassword = (value: string) => {
    this.state.draft.update((d) => ({ ...d, password: value }));
  };

  submit = () => this.#attempt(() => signUpWithEmail({ ...this.deps, ...this.state.draft.value }));

  continueWithGoogle = () => this.#attempt(() => signInWithGoogle(this.deps));

  continueWithApple = () => this.#attempt(() => signInWithApple(this.deps));

  async #attempt(fn: () => Promise<Result<void, SignInFailed | SignInCancelled>>): Promise<void> {
    if (this.state.busy.value) return;
    this.state.busy.value = true;
    this.state.error.value = null;
    try {
      const r = await fn();
      if (this.isDisposed) return;
      if (!r.ok && !hasError(r, SignInCancelled)) this.state.error.value = r.errors[0].message;
    } finally {
      this.state.busy.value = false;
    }
  }
}
